export const PDF_BRAND = {
  ink: [15, 20, 25],
  muted: [92, 102, 112],
  faint: [140, 148, 156],
  accent: [0, 102, 204],
  accentSoft: [232, 241, 252],
  good: [13, 143, 79],
  fair: [184, 110, 0],
  poor: [217, 72, 15],
  critical: [201, 42, 42],
  panel: [255, 255, 255],
  soft: [244, 246, 248],
  line: [226, 232, 240],
  gold: [216, 171, 18],
};

export async function tryLoadImageDataUrl(url) {
  if (!url) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () =>
        resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export function imageFormatFromDataUrl(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string') return null;
  const match = dataUrl.match(/^data:image\/([a-zA-Z0-9+.-]+);base64,/);
  if (!match) return null;
  const subtype = match[1].toLowerCase();
  if (subtype.includes('png')) return 'PNG';
  if (subtype.includes('jpg') || subtype.includes('jpeg')) return 'JPEG';
  if (subtype.includes('webp')) return 'WEBP';
  return null;
}

export function pageMetrics(doc, margin = 40) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  return {
    pageWidth,
    pageHeight,
    margin,
    contentWidth: pageWidth - margin * 2,
  };
}

export function ensureSpace(doc, y, needed, margin, onNewPage) {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + needed <= pageHeight - margin) return y;
  doc.addPage();
  return onNewPage ? onNewPage() : margin;
}

export async function drawPdfHeader(doc, business, options = {}) {
  const { margin, pageWidth } = pageMetrics(doc, options.margin ?? 40);
  const subtitle = options.subtitle || '';
  const badge = options.badge || '';
  const logo = await tryLoadImageDataUrl(business?.logo_url);
  const headerHeight = 104;

  doc.setFillColor(...PDF_BRAND.ink);
  doc.rect(0, 0, pageWidth, headerHeight, 'F');
  doc.setFillColor(...PDF_BRAND.accent);
  doc.rect(0, headerHeight - 3, pageWidth, 3, 'F');

  const brandX = margin;
  if (logo) {
    const format = imageFormatFromDataUrl(logo);
    if (format) doc.addImage(logo, format, brandX, 18, 52, 52, undefined, 'FAST');
  } else {
    doc.setFillColor(...PDF_BRAND.accent);
    doc.roundedRect(brandX, 18, 52, 52, 10, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text((business?.brand_name || 'W').slice(0, 1).toUpperCase(), brandX + 26, 50, {
      align: 'center',
    });
  }

  const textX = brandX + 64;
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(business?.brand_name || 'BW Station', textX, 38);
  if (subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(210, 220, 230);
    doc.text(subtitle, textX, 54);
  }

  const contact = [business?.address, business?.phone_primary, business?.support_email]
    .filter(Boolean)
    .join(' · ');
  if (contact) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(180, 190, 200);
    doc.text(contact, textX, 70, { maxWidth: pageWidth - textX - margin - 120 });
  }

  if (badge) {
    doc.setFillColor(...PDF_BRAND.accent);
    doc.roundedRect(pageWidth - margin - 118, 24, 118, 30, 15, 15, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(badge.toUpperCase(), pageWidth - margin - 59, 43, { align: 'center' });
  }

  return headerHeight + 18;
}

export function drawSectionTitle(doc, title, x, y, width) {
  doc.setFillColor(...PDF_BRAND.soft);
  doc.roundedRect(x, y, width, 28, 8, 8, 'F');
  doc.setTextColor(...PDF_BRAND.ink);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(title.toUpperCase(), x + 12, y + 18);
  return y + 38;
}

export function drawCard(doc, x, y, width, height) {
  doc.setDrawColor(...PDF_BRAND.line);
  doc.setFillColor(...PDF_BRAND.panel);
  doc.setLineWidth(0.6);
  doc.roundedRect(x, y, width, height, 10, 10, 'FD');
}

export function drawLabelValue(doc, label, value, x, y, valueMaxWidth) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...PDF_BRAND.muted);
  doc.text(label.toUpperCase(), x, y);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(...PDF_BRAND.ink);
  doc.text(value || '—', x, y + 13, { maxWidth: valueMaxWidth });
  return y + 30;
}

export function statusColor(status) {
  const value = (status || '').toLowerCase();
  if (value === 'critical') return PDF_BRAND.critical;
  if (value === 'poor') return PDF_BRAND.poor;
  if (value === 'fair') return PDF_BRAND.fair;
  return PDF_BRAND.good;
}

const STATUS_TINTS = {
  good: [232, 248, 238],
  fair: [255, 246, 224],
  poor: [255, 237, 224],
  critical: [255, 232, 232],
};

export function drawStatusPill(doc, status, x, y) {
  const key = (status || 'good').toLowerCase();
  const label = key.toUpperCase();
  const color = statusColor(status);
  const tint = STATUS_TINTS[key] || STATUS_TINTS.good;
  const width = Math.max(42, label.length * 5.5 + 14);
  doc.setFillColor(...tint);
  doc.setDrawColor(...color);
  doc.setLineWidth(0.5);
  doc.roundedRect(x, y - 10, width, 16, 8, 8, 'FD');
  doc.setTextColor(...color);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.text(label, x + width / 2, y, { align: 'center' });
  return width;
}

export function drawPdfFooter(doc, business, pageNumber, totalPages, margin = 40) {
  const { pageWidth, pageHeight, contentWidth } = pageMetrics(doc, margin);
  const y = pageHeight - 28;
  doc.setDrawColor(...PDF_BRAND.line);
  doc.setLineWidth(0.5);
  doc.line(margin, y - 8, margin + contentWidth, y - 8);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...PDF_BRAND.faint);
  const left = business?.brand_name || 'BW Station';
  const right = `Page ${pageNumber} of ${totalPages}`;
  doc.text(left, margin, y);
  doc.text(right, margin + contentWidth, y, { align: 'right' });
}

export function finalizePdfFooters(doc, business, margin = 40) {
  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page += 1) {
    doc.setPage(page);
    drawPdfFooter(doc, business, page, totalPages, margin);
  }
}
